import { findOne } from 'rcd-cpp-workspace/utils/array';
import { Fetch } from '.';
import { NodeType } from '../enums/NodeType';
import { SeeOption } from '../enums/SeeOption';
import { Tree } from '../enums/Tree';
import { DataMapping } from '../types/DataMapping';
import { Metadata, Schema, Workspace } from '../types/Metadata';
import { UserSettings } from '../types/UserSetting';
import { View } from '../types/View';
import { getSchema, isBlanks, isSpecialFolder } from '../utils';
import { Blanks } from '../utils/constants';
import { WorkflowStep } from '../types/WorkflowStep';
import { WorkflowStepType } from '../enums/WorkflowStepType';
import { WorkflowInfo } from '../types/WorkflowInfo';
import { ContentTypeConfig } from '../types/ContentTypeConfiguration';

interface Props {
  [x: string]: { [op: string]: any };
}
interface Node {
  nodeType: NodeType;
  props?: Props;
  relation?: string; // relation to previous node
}
export interface Payload {
  nodes: Node[];
  limit?: number;
}

export interface GetTreePayload {
  tree: string;
  treeHash?: string;
  treeNodeTypes?: NodeType[];
}

export const getTree =
  (f: Fetch) =>
  async (
    { tree, treeNodeTypes, treeHash }: GetTreePayload,
    schemas?: Schema[],
    field?: string
  ) => {
    const paramObj = {
      treeHash,
      nodeTypes: treeNodeTypes?.join(',')
    };
    if (tree == Tree.KeyInitiative && !!schemas) {
      schemas.forEach(s => {
        if (s.lastDataModifiedTime) {
          if (s.props.type == NodeType.KeyInitiative) {
            paramObj['ki'] = s.lastDataModifiedTime;
          } else if (s.props.type == NodeType.CoreTopic) {
            paramObj['ct'] = s.lastDataModifiedTime;
          }
        }
      });
    }
    if (
      [Tree.DemandQuestion, Tree.RolePriority].includes(tree as Tree) &&
      !!schemas
    ) {
      schemas.forEach(s => {
        if (s.lastDataModifiedTime) {
          if (s.props.type == NodeType.RolePriority) {
            paramObj['rp'] = s.lastDataModifiedTime;
          }
          if (s.props.type == NodeType.PriorityJourneyStep) {
            paramObj['pjs'] = s.lastDataModifiedTime;
          }
          if (
            s.props.type == NodeType.DemandQuestion &&
            tree == Tree.DemandQuestion
          ) {
            paramObj['demandquestion'] = s.lastDataModifiedTime;
          }
        }
      });
    }
    const params = Object.entries(paramObj)
      .map(([k, v]) => {
        if (k && v) return `${k}=${v}`;
        return null;
      })
      .filter(x => x);
    const data = await f.getJSON<Metadata[]>(
      `/metadata/tree${tree == Tree.Custom ? '' : '/' + tree}${
        params.length ? `?${params.join('&')}` : ''
      }`,
      {
        cache: 'force-cache',
        maxCacheHour: 4
      }
    );
    if (tree == Tree.Custom && schemas) {
      const displayFields = {};
      treeNodeTypes.forEach(nt => {
        const schema = getSchema(schemas, nt);
        if (schema?.props?.displayField) {
          displayFields[nt] = schema.props.displayField;
        }
      });
      if (Object.keys(displayFields).length) {
        setNameWithDisplayValue(data, displayFields);
      }
    }
    return data;
  };

const changeToUseStagedNameIfExisted = (data: Metadata[]) => {
  if (!data?.length) return data;
  return data.map(_ => {
    const d = { ..._, props: { ..._.props } };
    if (d.props?.stagedName) {
      d.props.name = d.props.stagedName;
    }
    if (d.labels.includes(NodeType.KeyInitiative)) {
      d.metadata = changeToUseStagedNameIfExisted(_.metadata);
    }
    return d;
  });
};

const setNameWithDisplayValue = (
  data: Metadata[],
  displayFields: { [n: string]: string }
) => {
  if (!data) return;
  data.forEach(d => {
    for (const label of d.labels) {
      if (d.props && displayFields[label]) {
        const displayValue = d.props[displayFields[label]];
        d.props.name = displayValue;
        break;
      }
    }
    setNameWithDisplayValue(d.metadata, displayFields);
  });
};

export const getAllTree = (f: Fetch) => () => {
  ['ki', 'fn', 'pn', 'dtyp', 'gtp', 'demandquestion'].forEach(tree => {
    f.getJSON<Metadata[]>('/metadata/tree/' + tree, {
      cache: 'force-cache'
    });
  });
};

export const getDataMapping = (f: Fetch) => async (params: string) => {
  const resp = await f.getJSON<DataMapping>(
    `/metadata/data/mapping${params ? `?${params}` : ''}`,
    { cache: 'force-cache' }
  );
  const role2Fn = {};
  const ki2Roles = {};
  const demandQuestion2Roles = {};
  if (resp?.fn2Roles) {
    Object.entries(resp.fn2Roles).forEach(([fnRefId, roleRefIds]) => {
      roleRefIds.forEach(roleRefId => {
        role2Fn[roleRefId] = fnRefId;
      });
    });
  }
  if (resp?.role2DemandQuestions) {
    Object.entries(resp.role2DemandQuestions).forEach(
      ([roleRefId, dqRefIds]) => {
        dqRefIds.forEach(dqRefId => {
          if (!demandQuestion2Roles[dqRefId])
            demandQuestion2Roles[dqRefId] = [];
          demandQuestion2Roles[dqRefId].push(roleRefId);
        });
      }
    );
  }
  if (resp?.role2KIs) {
    Object.entries(resp.role2KIs).forEach(([roleRefId, kiRefIds]) => {
      kiRefIds.forEach(kiRefId => {
        if (!ki2Roles[kiRefId]) ki2Roles[kiRefId] = [];
        ki2Roles[kiRefId].push(roleRefId);
      });
    });
  }
  const contentType2Roles = {};
  [resp?.role2ContentTypes, resp?.role2SubTypes].forEach(m => {
    if (!m) return;
    Object.entries(m).forEach(([roleRefId, contentTypeIds]) => {
      contentTypeIds.forEach(contentTypeId => {
        if (!contentType2Roles[contentTypeId])
          contentType2Roles[contentTypeId] = [];
        contentType2Roles[contentTypeId].push(roleRefId);
      });
    });
  });

  return {
    ...resp,
    role2Fn,
    ki2Roles,
    contentType2Roles,
    demandQuestion2Roles
  };
};

export const getPracticeCors =
  (f: Fetch) =>
  async (
    schemas?: Schema[]
  ): Promise<{ [practiceRefId: string]: Metadata }> => {
    const paramObj = {} as { [x: string]: any };
    if (schemas?.length) {
      const practiceSchema = getSchema(schemas, NodeType.Practice);
      if (practiceSchema?.lastDataModifiedTime) {
        paramObj['practicecor'] = practiceSchema.lastDataModifiedTime;
      }
    }
    const params = Object.entries(paramObj)
      .map(([k, v]) => {
        if (k && v) return `${k}=${v}`;
        return null;
      })
      .filter(x => x);

    return await f.getJSON<{ [practiceRefId: string]: Metadata }>(
      `/metadata/practice/cors${params.length ? `?${params.join('&')}` : ''}`,
      {
        cache: 'force-cache',
        maxCacheHour: 4
      }
    );
  };

export const getWorkspace = (f: Fetch) => async (refId: string) => {
  const w = await f.getJSON<Workspace>(`/metadata/workspace/${refId}`);
  if (w?.schemas?.length) {
    const getSchema = (t: string) => {
      for (const s of w.schemas) {
        if (s?.props?.type == t) return s;
      }
    };
    if (!w.folders) w.folders = [];
    w.folders.push({
      id: NodeType.Folder,
      labels: [NodeType.Folder],
      views: [],
      props: {
        name: 'My Favorites',
        isFavorite: true
      }
    });
    w.folders.sort((f1, f2) => {
      const sf1 = isSpecialFolder(f1);
      const sf2 = isSpecialFolder(f2);
      if (sf1 === sf2) return f1.props.name.localeCompare(f2.props.name);
      if (sf1) return -1;
      if (sf2) return 1;
      return 0;
    });
    w.folders
      .reduce((list, f) => {
        if (f.views?.length) return list.concat(f.views);
        return list;
      }, w.views || [])
      .forEach(view => {
        if (view?.props?.data && !view.dataSchema) {
          view.dataSchema = getSchema(view.props.data);
        }
      });
  }
  return w;
};

export const getWorkspaces = (f: Fetch) => async () =>
  f.getJSON<Workspace[]>(`/metadata/workspaces?v=${process.env.VERSION}`);

export const getUserSettings = (f: Fetch) => async () => {
  return await f.getJSON<UserSettings>('/metadata/settings');
};
export const getHomeWorkspace =
  (f: Fetch) => async (refId: string | number, dataType?: string) => {
    let q = 'refId=' + refId;

    try {
      const itemId = Number.parseInt(refId as any);
      if (`${refId}` == `${itemId}`) {
        q = 'itemId=' + itemId;
      }
    } catch (e) {
      console.error(`Failed to parse int for string `, refId);
    }
    if (dataType) {
      q += '&dataType=' + dataType;
    }
    return await f.getJSON<{
      homeWorkspace: Workspace;
      homeWorkspaceRefId: string;
      dataWorkspaceRefId: string;
      userWorkspaceId: string;
      refId: string;
      itemId: number;
      expiryDashboardRefId?: string;
      myItemsViewRefId?: string;
    }>(`/metadata/info/url?${q}`);
  };

export const getExpiryHome = (f: Fetch) => async (refId?: string | number) => {
  let q = null;

  if (q) {
    q = 'refId=' + refId;
  }
  try {
    const itemId = Number.parseInt(refId as any);
    if (!isNaN(itemId)) {
      q = 'itemId=' + itemId;
    }
  } catch (e) {
    console.error(`Failed to parse int for string `, refId);
  }
  return await f.getJSON<{
    homeWorkspace: Workspace;
    homeWorkspaceRefId: string;
    dataWorkspaceRefId: string;
    userWorkspaceId: string;
    refId: string;
    itemId: number;
    expiryDashboardRefId: string;
  }>(`/metadata/info/expiry-url?${!!q ? q : ''}`);
};

export const getViewInfo = (f: Fetch) => async (refId: string) =>
  f.getJSON<{
    view: View.Setting;
    inheritedByWorkspaces: Workspace[];
  }>(`/metadata/info/view?refId=${refId}`);

export const getComments = (f: Fetch) => async (refId: string) => {
  return await f.getJSON<any>(`/metadata/comments?srcRefId=${refId}`);
};

export const getUserExport = (f: Fetch) => async (refId: string) => {
  return await f.getJSON<
    Metadata<{ fileName: string; url: string; status: string; message: string }>
  >(`/metadata/export?refId=${refId}`);
};

export const getUserBackups = (f: Fetch) => async (empCd: number) => {
  const [resp1, resp2] = await Promise.all([
    f.getJSON<{ kiLeaders?: View.BackingUpFor[]; cors?: View.BackingUpFor[] }>(
      `/metadata/backups`
    ),
    f.graphQLFetch(process.env.CPP_PLATFORM_URI + '/user', {
      query: `query UserDelegate($employeeIds: [Int], $roleIds: [Int]){ 
          delegates(delegatedEmployeeIds: $employeeIds, delegatedRoleIds: $roleIds) { 
            empCd:employeeId name:employeeName roleId:delegatedRoleId 
          } 
        }`,
      variables: {
        employeeIds: [empCd],
        roleIds: [1, 2, 4, 8, 9, 11]
      }
    })
  ]);

  const backups: View.BackingUpFor[] = [];

  [...(resp1.kiLeaders || []), ...(resp1.cors || [])].forEach(
    ({ empCd, name, roleIds }) => {
      if (!roleIds || roleIds.length === 0) return;
      const existing = findOne(backups, { empCd });
      if (!existing) {
        backups.push({ empCd, name, roleIds: [...roleIds] });
      } else {
        roleIds.forEach(roleId => {
          if (!existing.roleIds.includes(roleId)) {
            existing.roleIds.push(roleId);
          }
        });
      }
    }
  );
  resp2.data?.delegates?.forEach(({ empCd, name, roleId }) => {
    if (!roleId) return;
    const b = findOne(backups, { empCd });
    if (!b) {
      backups.push({
        empCd,
        name,
        roleIds: [roleId]
      });
    } else {
      if (!b.roleIds.includes(roleId)) {
        b.roleIds.push(roleId);
      }
    }
  });
  backups.sort((a, b) => a?.name?.localeCompare(b?.name));
  return backups;
};

type SRPayload = {
  nodeType: NodeType;
  field?: string;
  fieldKey?: string;
  activeField?: string;
  orderField?: string;
  q?: string;
  ids?: string[];
  itemIds?: number[];
  excludeIds?: string[];
  limitToIds?: string[];
  itemType?: string;
  maxResults?: number;
  includeBlanks?: boolean;
  seeOption?: SeeOption;
  filterByContentTypeIds?: string[];
};

export const searchReferences = (f: Fetch) => async (payload: SRPayload) => {
  const results = await f.fetchJSON<Metadata[]>(payload, '/metadata/search');
  if (shouldIncludeBlanks(payload)) {
    results.unshift(Blanks);
  }
  return results;
};

const shouldIncludeBlanks = ({ ids, includeBlanks, q }: SRPayload) => {
  if (ids?.length && ids.filter(isBlanks).length > 0) return true;
  if (includeBlanks) {
    if (!q) return true;
    return Blanks.props.description.includes(q.toLowerCase());
  }
  return false;
};

export const getEFSDataDetail = (f: Fetch) => async (itemIds: number[]) => {
  try {
    const response = await f.fetchJSON<any>(
      { itemIds },
      `/metadata/efs/data/detail`
    );
    if (!response) {
      console.warn('No valid data returned from getEFSDataDetail');
      return [];
    }
    return response;
  } catch (error) {
    console.error('Error fetching EFS data detail:', error);
    throw error;
  }
};

export const getWorkflowSteps = (f: Fetch) => (type?: WorkflowStepType) =>
  f.getJSON<WorkflowStep[]>(
    `/metadata/workflow/steps${type ? `?type=${type}` : ''}`
  );

export const getWorkflowInfo = (f: Fetch) => async (refId: string) => {
  if (!refId) return null;
  return await f.getJSON<WorkflowInfo>(
    `/metadata/workflow/info?itemRefId=${refId}`
  );
};

export const getContentTypeConfigurations = (f: Fetch) => async () => {
  const results = await f.getJSON<ContentTypeConfig[]>('/metadata/ct/config', {
    cache: 'force-cache',
    maxCacheHour: 8
  });
  return results;
};

export const getAssociatedDelegates =
  (f: Fetch) => async (priorityJourneyStepRefId?: string) => {
    if (!priorityJourneyStepRefId) return [];
    return await f.getJSON<Metadata[]>(
      `/metadata/delegates/associated?priorityJourneyStepRefId=${priorityJourneyStepRefId}`
    );
  };

export const exportRolePriorityTree = (f: Fetch) => async () => {
  return await f.getJSON<any[]>('/metadata/export/kmap-data');
};
